import { useEffect, useMemo, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useToast } from "../../../hooks/use-toast";
import { Role } from "../../../types";
import { useCatalog } from "../hooks/use-catalog";
import { useCreateRole, useUpdateRole } from "../hooks/use-role-queries";

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: Role | null;
  ops: string;
  // The ops's enabled screens (ops_screen_config); the key picker is limited to these.
  enabledScreens: string[];
  onSuccess?: () => void;
}

// A group of selectable permission keys, labelled by the screen they belong to.
interface KeyGroup {
  label: string;
  keys: { key: string; label: string }[];
}

export function RoleFormDialog({
  open,
  onOpenChange,
  role,
  ops,
  enabledScreens,
  onSuccess,
}: RoleFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [apiError, setApiError] = useState("");

  const { toast } = useToast();
  const { data: catalog, isLoading: catalogLoading } = useCatalog();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? "");
    setDescription(role?.description ?? "");
    setPermissionKeys(role?.permissionKeys ?? []);
    setError("");
    setApiError("");
  }, [open, role]);

  // Build the key picker from the catalog: grantable keys (not public/baseline),
  // grouped by screen, but limited to the screens this ops actually has enabled.
  const groups = useMemo<KeyGroup[]>(() => {
    if (!catalog) return [];
    const labelByKey = new Map(
      catalog.permissions.map((p) => [p.key, p.label])
    );
    const grantable = new Set(
      catalog.permissions
        .filter((p) => !p.public && !p.baseline)
        .map((p) => p.key)
    );
    const enabled = new Set(enabledScreens);

    const result: KeyGroup[] = [];
    for (const screen of catalog.screens) {
      if (!enabled.has(screen.screen)) continue; // only the ops's enabled screens
      const keys = Array.from(
        new Set([...(screen.requires || []), ...(screen.actions || [])])
      ).filter((k) => grantable.has(k));
      if (keys.length === 0) continue;
      result.push({
        label: screen.label,
        keys: keys.map((k) => ({ key: k, label: labelByKey.get(k) ?? k })),
      });
    }
    return result;
  }, [catalog, enabledScreens]);

  const toggleKey = (key: string) => {
    setPermissionKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Nome é obrigatório");
      return;
    }
    setError("");
    setApiError("");

    try {
      if (role) {
        await updateRole.mutateAsync({
          id: role.id,
          payload: { name: name.trim(), description, permissionKeys },
        });
        toast({ title: "Sucesso", description: "Função atualizada!" });
      } else {
        await createRole.mutateAsync({
          ops,
          name: name.trim(),
          description,
          permissionKeys,
        });
        toast({ title: "Sucesso", description: "Função criada!" });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      const message = err?.message || "Erro ao salvar a função";
      setApiError(message);
      toast({ title: "Erro", description: message, variant: "destructive" });
    }
  };

  const isPending = createRole.isPending || updateRole.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {role ? "Editar Função" : "Nova Função"} — {ops}
          </DialogTitle>
          <DialogDescription>
            Defina o nome e as permissões concedidas por esta função.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {apiError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
              {apiError}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="role-name">Nome</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Operador de Checkout"
            />
            {error && <span className="text-sm text-red-500">{error}</span>}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="role-description">Descrição</Label>
            <Input
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Opcional"
            />
          </div>

          <div className="grid gap-3">
            <Label>Permissões</Label>
            {catalogLoading && (
              <span className="text-sm text-muted-foreground">
                Carregando catálogo...
              </span>
            )}
            {!catalogLoading && groups.length === 0 && (
              <span className="text-sm text-muted-foreground">
                Nenhuma tela habilitada para esta operação.
              </span>
            )}
            {groups.map((group) => (
              <div key={group.label} className="grid gap-2">
                <span className="text-xs font-semibold uppercase text-muted-foreground">
                  {group.label}
                </span>
                <div className="flex flex-wrap gap-2">
                  {group.keys.map(({ key, label }) => {
                    const selected = permissionKeys.includes(key);
                    return (
                      <Badge
                        key={key}
                        role="checkbox"
                        aria-checked={selected}
                        aria-label={label}
                        tabIndex={0}
                        variant={selected ? "default" : "outline"}
                        className="cursor-pointer select-none text-sm px-3 py-1.5"
                        onClick={() => toggleKey(key)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleKey(key);
                          }
                        }}
                      >
                        {label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending}>
            {isPending
              ? "Salvando..."
              : role
                ? "Salvar Alterações"
                : "Criar Função"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
